/** Page chrome: the public site header/footer and the signed-in app shell. Pure markup; state comes in as arguments. */
import { config } from '../core/config.js';
import { html } from '../core/html.js';
import { firstName } from '../core/format.js';
import { ROLE_LABEL } from '../core/constants.js';
import { homeFor } from '../core/session.js';
import { av, ic, logoMark } from '../ui/components.js';
import { NAV, PUBLIC_LINKS, isHeading } from './nav.js';
import { isDark } from './theme.js';
import { ago } from '../core/format.js';

export function publicLayout({ path, body, user, ui, bare = false }) {
  const active = (href) => (href === '/' ? path === '/' : path === href || path.startsWith(`${href}/`));
  return html`<a class="skip" href="#main">Skip to content</a>
  <nav class="pub-nav" aria-label="Site"><div class="in" style="position:relative">
    <a class="logo" href="/">${logoMark()}<span>${config.brandName}<small>by Vignova</small></span></a>
    <div class="pub-links ${ui.pubMenu ? 'open' : ''}" id="pub-links">${PUBLIC_LINKS.map(([href, label]) => html`<a href="${href}" class="${active(href) ? 'on' : ''}" ${active(href) ? html`aria-current="page"` : ''}>${label}</a>`)}
      ${user ? '' : html`<a class="m-only" href="/login">Log in</a>`}<a class="m-only" href="#" data-act="theme.toggle">${isDark() ? 'Light mode' : 'Dark mode'}</a></div>
    <span class="sp"></span>
    <button type="button" class="btn icon theme-t" aria-label="Toggle theme" data-act="theme.toggle">${ic(isDark() ? 'sun' : 'moon')}</button>
    ${user
      ? html`<a class="btn soft sm hide-m" href="${homeFor(user.role)}">My dashboard</a>`
      : html`<a class="btn ghost sm hide-m" href="/login">Log in</a><a class="btn sm" href="/join">Join<span class="hide-xs"> ${config.brandName}</span></a>`}
    <button type="button" class="btn icon pub-burger" aria-label="Menu" aria-expanded="${ui.pubMenu}" aria-controls="pub-links" data-act="ui.pubMenu">${ic(ui.pubMenu ? 'x' : 'menu')}</button>
  </div></nav>
  <main id="main" tabindex="-1" class="wrap">${body}</main>
  ${bare ? '' : footer()}`;
}

export function footer() {
  return html`<footer class="wrap foot"><div class="cols">
    <div><a class="logo" href="/" style="padding:0">${logoMark()}<span>${config.brandName}</span></a><p class="mt-s" style="max-width:320px">A controlled professional ecosystem by ${config.companyName}.</p></div>
    <div><h4>For you</h4><a href="/students">Students</a><a href="/freelancers">Freelancers</a><a href="/companies">Companies</a><a href="/startups">Startups</a></div>
    <div><h4>Product</h4><a href="/courses">All courses</a><a href="/requirements">Post a requirement</a><a href="/about">How matching works</a></div>
    <div><h4>Company</h4><a href="/about">About Vignova</a><a href="/contact">Contact</a><a href="/terms">Terms</a><a href="/privacy">Privacy</a></div>
  </div><p class="xs mt">© ${new Date().getFullYear()} ${config.companyName}</p></footer>`;
}

/** @param {{role:string, page:string, title:string, body:any, user:object, ui:object, notifications:object[], unread:number}} p */
export function appLayout({ role, page, title, body, user, ui, notifications, unread, navKey }) {
  const nav = (NAV[role] || []).filter((n) => config.recordings || n[0] !== 'recordings');
  const activeKey = navKey || page;
  const side = nav.map((item) => {
    if (isHeading(item)) return html`<div class="nav-g">${item[0]}</div>`;
    const [key, label, icon] = item;
    const on = activeKey === key;
    return html`<a class="nav-i ${on ? 'on' : ''}" href="/${role}/${key}" ${on ? html`aria-current="page"` : ''}>${ic(icon)}<span>${label}</span></a>`;
  });
  return html`<a class="skip" href="#main">Skip to content</a>
  <div class="shell">
    <div class="scrim ${ui.side ? 'open' : ''}" data-act="ui.closeSide"></div>
    <aside class="side ${ui.side ? 'open' : ''}" aria-label="Main navigation">
      <a class="logo" href="/">${logoMark()}<span>${config.brandName}<small>by Vignova</small></span></a>
      <div class="who">${av(user.name)}<div class="f1"><div class="b sm">${user.name}</div><div class="xs mu">${user.org_name || ROLE_LABEL[role]}</div></div></div>
      ${side}
      <div class="sp"></div>
      <button type="button" class="nav-i" data-act="auth.logout">${ic('out')}<span>Log out</span></button>
    </aside>
    <div class="main">
      <header class="top">
        <button type="button" class="btn icon burger" aria-label="Open menu" data-act="ui.openSide">${ic('menu')}</button>
        <h1>${title}</h1><span class="sp"></span>
        <button type="button" class="btn icon theme-t" aria-label="Toggle theme" data-act="theme.toggle">${ic(isDark() ? 'sun' : 'moon')}</button>
        <div class="bell" style="position:relative">
          <button type="button" class="btn icon" aria-label="Notifications${unread ? `, ${unread} unread` : ''}" aria-haspopup="true" aria-expanded="${ui.pop === 'bell'}" data-act="ui.pop" data-pop="bell">${ic('bell')}${unread ? html`<span class="dotn"></span>` : ''}</button>
          <div class="pop" id="pop-bell" ${ui.pop === 'bell' ? '' : 'hidden'}><div class="row between" style="padding:6px 10px"><b>Notifications</b>${unread ? html`<button type="button" class="linklike sm" data-act="notif.readAll">Mark all read</button>` : ''}</div>
            ${notifications.length
              ? notifications.map((n) => html`<div class="it ${n.read ? '' : 'unread'}" role="button" tabindex="0" data-act="notif.open" data-id="${n.id}" data-link="${n.link}">${ic('bell')}<span class="f1">${n.text}<span class="xs mu" style="display:block">${ago(n.at)}</span></span></div>`)
              : html`<p class="mu sm" style="padding:10px">You are all caught up.</p>`}</div>
        </div>
        <div style="position:relative">
          <button type="button" class="btn icon" aria-label="Account menu" aria-haspopup="true" aria-expanded="${ui.pop === 'me'}" data-act="ui.pop" data-pop="me" style="padding:2px">${av(user.name, 'sm')}</button>
          <div class="pop" id="pop-me" ${ui.pop === 'me' ? '' : 'hidden'} style="width:240px">
            <div style="padding:8px 10px"><div class="b">${user.name}</div><div class="xs mu">${user.email}</div></div>
            ${nav.some((n) => n[0] === 'profile') ? html`<a class="it" href="/${role}/profile">${ic('user')} Profile</a>` : ''}
            <a class="it" href="/${role}/account">${ic('gear')} Account and privacy</a>
            <button type="button" class="it" data-act="theme.toggle">${ic(isDark() ? 'sun' : 'moon')} ${isDark() ? 'Light mode' : 'Dark mode'}</button>
            <a class="it" href="/">${ic('home')} Public website</a>
            <button type="button" class="it" data-act="auth.logout">${ic('out')} Log out</button>
          </div>
        </div>
      </header>
      <main class="content" id="main" tabindex="-1">${body}</main>
    </div>
  </div>`;
}

export const greeting = (user) => firstName(user.name);
